// HoneyDo API Client - Enhanced with Tim-Combo patterns
import { ApiResponse, isSuccessResponse } from '../types/api';
import { honeyDoApiClient, Make_HoneyDo_API_Call } from './apiClient';

// Legacy wrapper class for backward compatibility
class ApiClient {
  // Use the new enhanced API client under the hood
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const method = (options.method as 'GET' | 'POST' | 'PUT' | 'DELETE') || 'GET';
    const body = options.body ? JSON.parse(options.body as string) : undefined;

    const response = await Make_HoneyDo_API_Call(endpoint, method, body, {
      headers: options.headers
    }) as any;
    
    // Convert new response format to legacy format for compatibility
    if (isSuccessResponse(response)) {
      return {
        success: response.success,
        message: response.message,
        data: (response.data.Records?.[0] || response.data.Records || response.data) as T,
        metadata: {
          total_records: response.meta.Total_Records,
          request_id: response.meta.Request_ID,
          query_context: response.data.Query_Context
        }
      };
    } else {
      // Convert error response to legacy format
      throw new Error(response.message);
    }
  }

  // Test API connection
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await Make_HoneyDo_API_Call('/health', 'GET') as any;
      return { success: response.success, message: response.message };
    } catch (error) {
      return { success: false, message: 'Connection failed' };
    }
  }

  // Register user with backend after Cognito registration
  async registerUser(userData: {
    email: string;
    display_name: string;
    user_type: string;
    age_range?: string;
    family_name: string;
  }) {
    return Make_HoneyDo_API_Call('/auth/register', 'POST', userData) as any;
  }

  // User Profile
  async getUserProfile(userId: string) {
    return Make_HoneyDo_API_Call(`/users/profile/${userId}`, 'GET') as any;
  }

  // Get current user profile using JWT
  async getCurrentUserProfile(): Promise<ApiResponse<any>> {
    try {
      const response = await Make_HoneyDo_API_Call('/users/profile', 'GET') as any;
      return {
        success: response.success,
        message: response.message,
        data: response.data.Records?.[0] || response.data.Records || response.data
      };
    } catch (error) {
      throw error;
    }
  }

  // Family Members
  async getFamilyMembers(familyId: string) {
    return Make_HoneyDo_API_Call(`/family/${familyId}/members`, 'GET') as any;
  }

  // Tasks
  async getTasks(familyId: string) {
    return Make_HoneyDo_API_Call(`/family/${familyId}/tasks`, 'GET') as any;
  }

  async createTask(taskData: any) {
    return Make_HoneyDo_API_Call('/tasks', 'POST', taskData) as any;
  }

  async updateTask(taskId: string, updates: {
    status?: string;
    completion_notes?: string;
    assigned_to?: string;
    priority_level?: number;
  }) {
    return Make_HoneyDo_API_Call(`/tasks/${taskId}`, 'PUT', updates) as any;
  }

  async getTask(taskId: string) {
    return Make_HoneyDo_API_Call(`/tasks/${taskId}`, 'GET') as any;
  }

  // Get user's assigned tasks
  async getMyTasks(): Promise<ApiResponse<any[]>> {
    try {
      const response = await Make_HoneyDo_API_Call('/tasks', 'GET') as any;
      return {
        success: response.success,
        message: response.message,
        data: response.data.Records || []
      };
    } catch (error) {
      throw error;
    }
  }

  // Family management methods - Real API calls (NO MOCKS)
  async getMyFamily(): Promise<ApiResponse<any>> {
    try {
      // Get user's families via ENTITIES.LIST endpoint
      const response = await Make_HoneyDo_API_Call('/entities', 'GET', undefined, {
        params: { type: 'family' }
      }) as any;

      if (isSuccessResponse(response)) {
        const families = response.data?.Records || [];
        // Return first family (most users have one family)
        return {
          success: true,
          message: 'Family retrieved',
          data: families.length > 0 ? families[0] : null
        };
      } else {
        throw new Error(response.message || 'Failed to fetch user families');
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to retrieve family');
    }
  }

  async createFamily(data: { family_name: string }): Promise<ApiResponse<any>> {
    try {
      // Create family via FAMILY.CREATE endpoint
      const response = await Make_HoneyDo_API_Call('/family', 'POST', {
        name: data.family_name,
        settings: {}
      }) as any;

      if (isSuccessResponse(response)) {
        return {
          success: true,
          message: 'Family created successfully',
          data: response.data?.Records?.[0] || response.data
        };
      } else {
        throw new Error(response.message || 'Failed to create family');
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create family');
    }
  }

  async inviteFamilyMember(data: {
    family_id?: string;
    email: string;
    display_name?: string;
    user_type: string;
    message?: string;
  }): Promise<ApiResponse<any>> {
    try {
      // Send invitation via FAMILY.INVITE endpoint
      // Using exact database field names from family_invitations table
      const response = await Make_HoneyDo_API_Call('/family/invite', 'POST', {
        family_id: data.family_id,
        email: data.email,
        invited_user_type: data.user_type,  // Maps to database field
        invited_family_role: null,  // Optional, database allows NULL
        invitation_message: data.message || null  // Maps to database field
      }) as any;

      if (isSuccessResponse(response)) {
        return {
          success: true,
          message: 'Invitation sent successfully',
          data: response.data?.Records?.[0] || response.data
        };
      } else {
        throw new Error(response.message || 'Failed to send invitation');
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to send invitation');
    }
  }

  async removeFamilyMember(familyId: string | undefined, memberId: string): Promise<ApiResponse<any>> {
    try {
      if (!familyId) {
        throw new Error('Family ID is required to remove member');
      }

      // Remove member via FAMILY.REMOVE_MEMBER endpoint
      const response = await Make_HoneyDo_API_Call(
        `/family/${familyId}/members/${memberId}`,
        'DELETE'
      ) as any;

      if (isSuccessResponse(response)) {
        return {
          success: true,
          message: 'Member removed successfully',
          data: response.data || {}
        };
      } else {
        throw new Error(response.message || 'Failed to remove family member');
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to remove family member');
    }
  }

  // Remove stub implementations - these should be implemented when the features are ready
  // or throw meaningful errors to make failures visible
  async createMaintenanceSchedule(schedule: any): Promise<any> {
    throw new Error('Maintenance schedule creation is not yet implemented. Contact support.');
  }

  async getUsers(params: any): Promise<any[]> {
    throw new Error('User listing is not yet implemented. Contact support.');
  }

  async getWeatherForecast(params: any): Promise<any[]> {
    const { Make_Authorized_API_Call } = await import('./apiClient');
    const response = await Make_Authorized_API_Call('/scheduling/weather', 'GET', undefined, { params });
    return response?.data?.Records || response?.Records || [];
  }

  async getMaintenanceSchedules(params: any): Promise<any[]> {
    throw new Error('Maintenance schedules listing is not yet implemented. Contact support.');
  }

  // Add HTTP method wrappers for services that need them
  async get(endpoint: string, options?: any): Promise<any> {
    const { Make_Authorized_API_Call } = await import('./apiClient');
    return Make_Authorized_API_Call(endpoint, 'GET', undefined, options);
  }

  async post(endpoint: string, body?: any, options?: any): Promise<any> {
    const { Make_Authorized_API_Call } = await import('./apiClient');
    return Make_Authorized_API_Call(endpoint, 'POST', body, options);
  }

  async put(endpoint: string, body?: any, options?: any): Promise<any> {
    const { Make_Authorized_API_Call } = await import('./apiClient');
    return Make_Authorized_API_Call(endpoint, 'PUT', body, options);
  }

  async patch(endpoint: string, body?: any, options?: any): Promise<any> {
    const { Make_Authorized_API_Call } = await import('./apiClient');
    return Make_Authorized_API_Call(endpoint, 'PATCH', body, options);
  }

  async delete(endpoint: string, options?: any): Promise<any> {
    const { Make_Authorized_API_Call } = await import('./apiClient');
    return Make_Authorized_API_Call(endpoint, 'DELETE', undefined, options);
  }
}

export const apiClient: ApiClient = new ApiClient();

// Re-export from apiClient.ts for backward compatibility
export { Make_Authorized_API_Call, API_ENDPOINTS, isSuccessResponse, isErrorResponse } from './apiClient';